import { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare, Trash } from "react-bootstrap-icons";
import Swal from 'sweetalert2';
import api from "../api/axios";
import GoogleMapPolygon from "../components/GoogleMapPolygon";


export default function PlacesGeoRef() {
    const [search, setSearch] = useState("");
    const [filteredPlaces, setFilteredPlaces] = useState([]);
    const [places, setPlaces] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [errors, setErrors] = useState({});
    const [loadingSave, setLoadingSave] = useState(false);

    const [formData, setFormData] = useState({
        id: null,
        name: '',
        kind: "",
        is_active: null,
        is_motel: null,
        geometry: [],
    });

    const normalizeText = (text) => {
        return text
        ?.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    useEffect(() => {
  const term = normalizeText(search).trim();

  const result = places.filter((p) => {
    // ======================
    // TEXTO NORMAL
    // ======================
    const matchesText =
      normalizeText(p.name)?.includes(term);

    // ======================
    // TIPO (POINT, ZONE, etc)
    // ======================
    const kindLabel = normalizeText(TYPE_LABELS[p.kind] || "");
    const matchesKind = kindLabel.includes(term);

    // ======================
    // ESTADO
    // ======================
    const matchesStatus =
      ("activo".startsWith(term) && p.is_active === true) ||
      ("inactivo".startsWith(term) && p.is_active === false);

    // 👇 input vacío → todo
    if (!term) return true;

    // 👇 prioridad a estado
    if ("activo".startsWith(term) || "inactivo".startsWith(term)) {
      return matchesStatus;
    }

    // 👇 texto o tipo
    return matchesText || matchesKind;
  });

  setFilteredPlaces(result);
}, [search, places]);


    // ==============================
    // Fetch inicial
    // ==============================
    useEffect(() => {
        fetchPlaces();
    }, []);
    
    const fetchPlaces = async () => {
        try {
            setLoadingTable(true);
            const res = await api.get("/PlacesGeoRef");
            setFilteredPlaces(res.data.data);
            setPlaces(res.data.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingTable(false);
        }
    }

    const handleClose = () => {
        setShowModal(false);
        setShowDeleteModal(false);
        setIsEditing(false);
    };
    
    const handleShowEdit = (place) => {
        const hasMotelWord = normalizeText(place.name)?.includes("motel");

        setIsEditing(true);
        setErrors({});
        setFormData({
            id: place.id,
            name: place.name,
            kind: place.kind,
            is_active: place.is_active,
            is_motel: hasMotelWord,
            geometry: place.geometry
        });

        setShowModal(true);
    }
    
    const handleShowDelete = (place) => {
        setErrors({});
        setFormData({
            id: place.id,
            name: place.name,
            kind: place.kind,
            is_active: place.is_active,
            geometry: place.geometry
        });

        setShowDeleteModal(true);
    }
    
    const handleShowNew = () => {
        setIsEditing(false);
        setErrors({});
        setFormData({
            id: null,
            name: '',
            kind: "",
            is_active: true,
            is_motel: false,
            geometry: [],
        });
        setShowModal(true);
    }
    
    // ==============================
    // Guardar
    // ==============================
    const handleSave = async () => {
        setLoadingSave(true);
        setErrors({});

        const payload = {
            ...formData
        };
        
        try {
            if (isEditing) {
                await api.put(`/PlacesGeoRef/${formData.id}`, payload);
            } else {
                await api.post("/PlacesGeoRef", payload);
            }
            handleClose();
            fetchPlaces();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else {
            Swal.fire({
                title: 'Error',
                text: "Error desconocido al guardar",
                icon: 'error',
                timer: 1500,
                showConfirmButton: false
            });
            }
        } finally {
            setLoadingSave(false);
        }
    };

    // ==============================
    // Eliminar
    // ==============================
    const handleDelete = async () => {
        setLoadingSave(true);
        setErrors({});

        const payload = {
            ...formData
        };
        try {
            await api.delete(`/PlacesGeoRef/${formData.id}`, payload);
            
            handleClose();
            fetchPlaces();
        } catch (err) {
            handleClose();
            if (err.response?.status === 422) {
                console.log('errores', err.response.data.message);
                
                Swal.fire({
                    title: 'Error',
                    text: err.response.data.message,
                    icon: 'error',
                    showConfirmButton: true
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: "Error desconocido al guardar",
                    icon: 'error',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        } finally {
            setLoadingSave(false);
        }
    };

    const TYPE_LABELS = {
        POINT: "Origen/Destino",
        ZONE: "Zona",
        ORIGIN: "Origen",
        DESTINATION: "Destino",
    };

    const TYPE_HELP_TEXT = {
        ZONE: "El valor/recargo se evalúa y aplica cada vez que el vehículo ingresa o circula dentro de esta zona durante el recorrido.",
        ORIGIN: "El valor/recargo se evalúa solo al iniciar el recorrido, si el vehículo se encuentra dentro de este polígono en el momento de la salida.",
        DESTINATION: "El valor/recargo se evalúa solo al finalizar el recorrido, si el vehículo se encuentra dentro de este polígono en el momento de la llegada.",
        POINT: "El valor/recargo se evalúa tanto al iniciar como al finalizar el recorrido, pero solo se aplica una vez, incluso si se cumple en ambos casos.",
    };

    
    // ==============================
    // Columnas tabla
    // ==============================
    const columns = [
        
        { name: "Nombre", selector: (row) => row.name, sortable: true, wrap: true },
        {
            name: "Tipo",
            cell: (row) => (
                <span className="fw-semibold">
                {TYPE_LABELS[row.kind] ?? row.kind}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Estado",
            cell: (row) => (
                <span
                className={`badge ${row.is_active ? "bg-success" : "bg-danger"}`}
                style={{ fontSize: "0.75rem" }}
                >
                {row.is_active ? "Activo" : "Inactivo"}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Opciones",
            cell: (row) => (
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleShowEdit(row)}
                    >
                        <PencilSquare />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleShowDelete(row)}
                    >
                        <Trash />
                    </Button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    const handlePolygonComplete = (coords) => {
        console.log("Coordenadas recibidas en el padre:", coords);
        setFormData({ ...formData, geometry: coords })
        // Aquí puedes guardarlas en el estado del padre o enviarlas al backend
    };

    return (
        <div className="mt-4">

            {/* TABLA */}
            <DataTable
                title="Lugares"
                columns={columns}
                data={filteredPlaces}
                pagination
                highlightOnHover
                striped
                progressPending={loadingTable}
                progressComponent={
                    <div className="text-center p-3">
                        <div className="spinner-border text-primary"></div>
                        <p>Cargando...</p>
                    </div>
                }
                subHeader
                subHeaderComponent={
                <div className="d-flex justify-content-between w-100">
                    <Button variant="outline-success" size="sm" onClick={handleShowNew}>
                    Nuevo <Plus />
                    </Button>

                    <input
                    type="text"
                    className="form-control w-25"
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                }
            />
            
            {/* MODAL */}
            <Modal show={showModal} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Editar Lugar" : "Nuevo Lugar"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        <div className="row">
                            {/* Nombre */}
                            <Form.Group className="mb-3 col-6">
                                <Form.Label>Nombre</Form.Label>
                                <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                />
                                {errors.name?.map((e, i) => (
                                <Form.Text key={i} className="text-danger d-block">
                                    {e}
                                </Form.Text>
                                ))}
                            
                            </Form.Group>
                            {/* Tipo */}
                            <Form.Group className="mb-3 col-6">
                                <Form.Label>Tipo</Form.Label>
                                <Form.Select
                                    value={formData.kind}
                                    onChange={(e) =>
                                    setFormData({ ...formData, kind: e.target.value })
                                    }
                                >
                                    <option value="">Selecciona un tipo</option>
                                    <option value="ZONE">Zona</option>
                                    <option value="ORIGIN">Origen</option>
                                    <option value="DESTINATION">Destino</option>
                                    <option value="POINT">Origen/Destino</option>
                                </Form.Select>

                                {errors.kind?.map((e, i) => (
                                    <Form.Text key={i} className="text-danger d-block">
                                    {e}
                                    </Form.Text>
                                ))}
                            </Form.Group>
                        </div>
                        <div className="row">
                            {/* Estado */}
                            <Form.Group className="mb-3 col-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Activo"
                                    checked={formData.is_active}
                                    onChange={(e) =>{
                                        setFormData({ ...formData, is_active: e.target.checked });
                                    }}
                                />
                            </Form.Group>
                            {/* Motel? */}
                            <Form.Group className="mb-3 col-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Motel"
                                    checked={formData.is_motel}
                                    onChange={(e) =>{
                                        setFormData({ ...formData, is_motel: e.target.checked });
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3 col-6">
                                {formData.kind && (
                                    <Form.Text className="text-muted">
                                        {TYPE_HELP_TEXT[formData.kind]}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </div>
                    
                        {/* Geometria */}
                        <Form.Group className="mb-4">
                            {errors.geometry?.map((e, i) => (
                                <Form.Text key={i} className="text-danger d-block">
                                {e}
                                </Form.Text>
                            ))}
                            <GoogleMapPolygon onPolygonComplete={handlePolygonComplete} defaultPolygon={formData.geometry} />
                        </Form.Group>
                    
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={loadingSave}>
                    {loadingSave ? (
                        <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Guardando...
                        </>
                    ) : isEditing ? (
                        "Actualizar"
                    ) : (
                        "Guardar"
                    )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DELETE */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminación de Lugar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Al borrar el lugar <strong>{formData.name}</strong> no se podrá recuperar ¿Desea continuar?.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loadingSave}>
                    {loadingSave ? (
                        <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Cargando...
                        </>
                    ) :  (
                        "Eliminar"
                    )}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );

}